// src/pages/CustomerRidePreview.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import RideBookingModal from "../components/RideBookingModal";

const API_BASE = process.env.REACT_APP_API_BASE;

/* =========================================================
   🔥 Normalize booking status from backend
   backend returns: requestStatus | rideStatus | status
========================================================= */
const getBookingStatus = (booking) => {
  if (!booking) return null;
  return (
    booking.requestStatus ||
    booking.rideStatus ||
    booking.status ||
    null
  );
};

const CustomerRidePreview = () => {
  const { rideId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [ride, setRide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [booking, setBooking] = useState(null);

  const customerId = localStorage.getItem("customerId");

  // Values passed after payment success
  const passedFinalPrice = location.state?.finalPrice || null;
  const passedFrom = location.state?.from || null;
  const passedTo = location.state?.to || null;
  const paymentSuccess = location.state?.paymentSuccess || false;

  /* =========================================================
     📡 Load Ride + Booking (if any)
  ========================================================= */
  useEffect(() => {
    const fetchRide = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/ride/preview/${rideId}`);
        setRide(res.data);
      } catch (err) {
        console.error("❌ Error fetching ride:", err);
        setError("Ride not found.");
      } finally {
        setLoading(false);
      }
    };

    const fetchBooking = async () => {
      if (!rideId || !customerId) return;
      try {
        const res = await axios.get(
          `${API_BASE}/api/ridepayment/check-booking?rideId=${rideId}&customerId=${customerId}`
        );
        if (res.data?.booking || res.data?.exists) {
          setBooking(res.data.booking);
        }
      } catch {
        setBooking(null);
      }
    };

    fetchRide();
    fetchBooking();
  }, [rideId, customerId]);

  /* =========================================================
     ⏳ Loading / Error State
  ========================================================= */
  if (loading)
    return <div className="text-center py-10 text-gray-600">Loading ride details...</div>;
  if (error || !ride)
    return <div className="text-center text-red-500 py-10">{error}</div>;

  /* =========================================================
     🚗 Ride Details
  ========================================================= */
  const {
    from,
    to,
    date,
    time,
    pricePerSeat,
    dropOffNotes,
    seatsAvailable,
    pickupLocation,
    stops,
    rideOptions,
    workerId,
  } = ride;

  const carDetails = workerId?.driverProfile
    ? `${workerId.driverProfile.make} ${workerId.driverProfile.model} (${workerId.driverProfile.year})`
    : "Vehicle details not available";

  const displayFrom = passedFrom || from;
  const displayTo = passedTo || to;
  const displayPrice = passedFinalPrice || pricePerSeat;
  const fixedDate = new Date(date?.replace(/-/g, "/"));

  const bookingStatus = getBookingStatus(booking);

  /* =========================================================
     ⭐ Final Booking Button Logic — FIXED
  ========================================================= */
  const renderBookingButton = () => {
    // If payment just went through but booking isn't created yet
    if (!booking && paymentSuccess) {
      return (
        <div className="flex-1 text-center bg-blue-100 text-blue-700 font-semibold py-2 rounded-full">
          Processing payment… Please wait
        </div>
      );
    }

    // No booking yet
    if (!bookingStatus) {
      return (
        <button
          onClick={() => setShowModal(true)}
          className="flex-1 bg-green-600 text-white py-2 rounded-full hover:bg-green-700 transition"
        >
          ✅ Request to Book
        </button>
      );
    }

    // Status: PENDING
    if (bookingStatus === "pending") {
      return (
        <div className="flex-1 text-center text-yellow-600 font-semibold py-2">
          🚕 Booking pending...
        </div>
      );
    }

    // Status: ACCEPTED
    if (bookingStatus === "accepted") {
      return (
        <div className="flex-1 text-center text-green-600 font-semibold py-2">
          ✅ Ride Confirmed
        </div>
      );
    }

    // Status: DECLINED
    if (bookingStatus === "declined") {
      return (
        <div className="flex-1 text-center text-red-600 font-semibold py-2">
          ❌ Request Declined
        </div>
      );
    }

    // Status: REFUNDED
    if (bookingStatus === "refunded") {
      return (
        <div className="flex-1 text-center text-gray-600 font-semibold py-2">
          💸 Ride Refunded
        </div>
      );
    }

    // Status: DISPUTED
    if (bookingStatus === "disputed") {
      return (
        <div className="flex-1 text-center text-orange-600 font-semibold py-2">
          ⚠️ Ride in dispute
        </div>
      );
    }

    // Default fallback
    return (
      <button
        onClick={() => setShowModal(true)}
        className="flex-1 bg-green-600 text-white py-2 rounded-full hover:bg-green-700 transition"
      >
        ✅ Request to Book
      </button>
    );
  };

  /* =========================================================
     UI
  ========================================================= */
  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-blue-100 py-10 px-4 font-[Poppins]">
      <div className="max-w-2xl mx-auto space-y-10">

        {/* TITLE */}
        <div className="text-center">
          <h1 className="text-5xl sm:text-6xl font-extrabold text-gray-900 drop-shadow-md">
            BYN Rides
          </h1>
          <p className="mt-3 text-lg sm:text-xl text-gray-600 font-medium tracking-wide">
            Book smarter. Ride better. Every trip matters.
          </p>
        </div>

        {/* MAIN RIDE CARD */}
        <div className="bg-white p-6 rounded-2xl shadow-lg space-y-3">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-lg font-bold text-gray-900">{displayFrom}</h2>
              <p className="text-sm text-gray-500">{displayFrom}</p>

              <h2 className="text-lg font-bold text-gray-900 mt-2">{displayTo}</h2>
              <p className="text-sm text-gray-500">{displayTo}</p>
            </div>

            <div className="text-right">
              <p className="text-sm font-medium text-gray-700">
                {format(fixedDate, "EEE, MMM dd")} at {time}
              </p>
              <p className="text-blue-600 font-bold text-xl mt-1">
                ${displayPrice}
                <span className="text-sm font-normal text-gray-600"> / seat</span>
              </p>

              <p className="text-sm text-gray-600 mt-1">
                {Number.isInteger(seatsAvailable) ? seatsAvailable : 0} seats left
              </p>
            </div>
          </div>

          <div className="flex gap-2 mt-2">
            {Number.isInteger(seatsAvailable) && seatsAvailable > 0 ? (
              [...Array(seatsAvailable)].map((_, i) => (
                <div key={i} className="w-7 h-7 bg-gray-200 rounded-full flex justify-center items-center">
                  💺
                </div>
              ))
            ) : (
              <div className="text-xs text-gray-400 italic">No seat info</div>
            )}
          </div>

          {dropOffNotes && (
            <p className="italic text-sm text-gray-500">"{dropOffNotes}"</p>
          )}
        </div>

        {/* PICKUP & STOPS */}
        <div className="bg-white p-6 rounded-2xl shadow-lg">
          <h3 className="font-bold mb-2 text-gray-900 text-md">Pickup Info</h3>

          <p className="text-sm text-gray-700 mb-2">
            <strong>Main pickup:</strong> {pickupLocation}
          </p>

          {Array.isArray(stops) && stops.length > 0 && (
            <div className="space-y-2 text-sm">
              {stops.map((stop, i) => (
                <div key={i} className="bg-gray-100 p-3 rounded-md">
                  <p><strong>Stop:</strong> {stop.name}</p>
                  <p><strong>Pickup:</strong> {stop.pickup}</p>
                  <p><strong>Time:</strong> {stop.time}</p>
                  <p><strong>Price:</strong> ${stop.price}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* DRIVER INFO */}
        <div className="bg-white p-6 rounded-2xl shadow-lg space-y-4">
          <div className="flex items-center gap-4">
            <img
              src={
                workerId?.profilePhotoUrl
                  ? `${API_BASE}${workerId.profilePhotoUrl}`
                  : "/default-avatar.png"
              }
              alt="Driver"
              className="w-16 h-16 rounded-full object-cover border"
            />

            <div>
              <p className="text-md font-bold text-gray-900">{workerId?.name || "Driver"}</p>
              <p className="text-sm text-blue-600">✅ Driver’s license verified</p>
              <p className="text-sm text-gray-700">{carDetails}</p>
            </div>
          </div>

          {workerId?.driverProfile?.carPhotoUrl && (
            <div className="mt-3">
              <h4 className="text-sm font-semibold text-gray-700 mb-1">Driver’s Vehicle</h4>
              <img
                src={`${API_BASE}${workerId.driverProfile.carPhotoUrl}`}
                alt="Car"
                className="w-full max-h-56 object-cover rounded-xl border shadow-sm"
              />
            </div>
          )}
        </div>

        {/* RIDE OPTIONS */}
        <div className="bg-white p-6 rounded-2xl shadow-lg space-y-2 text-sm text-gray-800">
          <h3 className="font-bold mb-2">Ride Rules & Conditions</h3>

          {rideOptions?.twoPerRow && <div>👥 Max 2 people per row</div>}
          {rideOptions?.luggage && <div>🧳 Luggage: {rideOptions.luggage}</div>}
          {rideOptions?.winterTires ? <div>❄️ Winter tires equipped</div> : <div>❄️ No winter tires</div>}
          {rideOptions?.petsAllowed ? <div>🐶 Pets allowed</div> : <div>🐶 No pets</div>}
          {rideOptions?.bikesAllowed ? <div>🚲 Bikes allowed</div> : <div>🚲 No bikes</div>}
          {rideOptions?.skisAllowed ? <div>🎿 Skis allowed</div> : <div>🎿 No skis</div>}
        </div>

        {/* ACTION BUTTONS — FIXED */}
        <div className="bg-white p-6 rounded-2xl shadow-lg flex flex-col sm:flex-row gap-4 justify-between">
          <button
            onClick={() =>
  navigate(`/customer/ride-chat/${rideId}`, {
    state: {
      from: displayFrom,
      to: displayTo,
      price: displayPrice,
      isStop: Boolean(passedFrom || passedTo || passedFinalPrice)
    }
  })
}

            className="flex-1 bg-gray-800 text-white py-2 rounded-full hover:bg-black transition"
          >
            💬 Chat with Driver
          </button>

          {renderBookingButton()}
        </div>

        {/* BOOKING MODAL */}
        {showModal && (
          <RideBookingModal
            onClose={() => setShowModal(false)}
            rideId={rideId}
            from={displayFrom}
            to={displayTo}
            finalPrice={displayPrice}           // ⭐ The correct $20 stop price
            stopName={displayFrom}              // or stop.name if you want
            stopPrice={displayPrice}            // ⭐ Pass the same price
          />
        )}

      </div>
    </div>
  );
};

export default CustomerRidePreview;
